// Third-party
import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Types
import { SuitabilityAnalysisState } from '../../../../redux/modules/types/suitability-analysis';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { getSuitabilityAnalysis, GET_SUITABILITY_ANALYSIS } from '../../../../redux/modules/suitability-analysis';
import { selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';

// Components
import { CancelButton } from '../../buttons';
import { Breadcrumb, BreadcrumbItem } from '../../navigation';
import { HoldingIcon, LoadingSpinner } from '../../common';
import PortfoliosTable from './PortfoliosTable';
import DetailedPortfolio from './DetailedPortfolio';
import Modal from '@/views/components/modals/AccessibleModal';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import ComparisonItem from '@/types/comparison-item';
import SuitabilityAnalysisPortfolio from '@/types/suitability-analysis-portfolio';
import Nullable from '@/types/nullable';
import ExtendedSuitabilityAnalysisPortfolio from '@/types/extended-suitability-analysis-portfolio';
import ExtendedAnalysisPortfolioItem from '@/types/extended-analysis-portfolio-item';

type StateProps = {
  isSuitabilityAnalysisLoading: boolean;
  suitabilityAnalysis: SuitabilityAnalysisState;
};

type DispatchProps = {
  getSuitabilityAnalysis: (portfolios: ComparisonItem[], contactId?: string) => Promise<SuitabilityAnalysisPortfolio[]>;
};

type Props = DispatchProps &
  StateProps & {
    className?: string;
    contactId?: string;
    onCancel: () => void;
    portfolios: ComparisonItem[];
    show: boolean;
    submitButton?: React.ReactNode;
  };

type State = {
  detailedPortfolio: Nullable<ExtendedSuitabilityAnalysisPortfolio>;
};

class ProductSuitabilityModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      detailedPortfolio: null,
    };
  }

  mergePorfolioAnalysisResults = (suitabilityAnalysis: SuitabilityAnalysisState, portfolios: ComparisonItem[]) =>
    portfolios.map(portfolio => {
      const suitabilityPortfolio = (suitabilityAnalysis || []).find(p => p.id === portfolio._id);
      const suitabilityPortfolioItems = portfolio.holdings.map(holding => {
        const suitabilityPortfolioItem = suitabilityPortfolio?.items.find(pi => pi.id === holding.id);

        return {
          ...suitabilityPortfolioItem,
          name: holding?.name,
          weight: holding?.weight,
        } as ExtendedAnalysisPortfolioItem;
      });

      return {
        ...suitabilityPortfolio,
        color: portfolio?.color,
        items: suitabilityPortfolioItems,
        name: portfolio?.name,
        riskProfile: portfolio?.riskProfile,
        type: COMPARISON_ITEM_TYPES.PORTFOLIO,
      } as ExtendedSuitabilityAnalysisPortfolio;
    });

  generateBreadcrumbs = (): BreadcrumbItem[] => {
    const { detailedPortfolio } = this.state;
    const breadcrumbs: BreadcrumbItem[] = [
      {
        value: formatMessage('ProductSuitability.Breadcrumbs.Summary'),
        onClick: () => this.setState({ detailedPortfolio: null }),
      },
    ];
    if (detailedPortfolio) {
      return [
        ...breadcrumbs,
        {
          value: (
            <>
              <HoldingIcon type={detailedPortfolio.type} color={detailedPortfolio.color} />
              {formatLocalizedString(detailedPortfolio.name)}
            </>
          ),
        },
      ];
    }

    return breadcrumbs;
  };

  renderBody = () => {
    const { isSuitabilityAnalysisLoading } = this.props;
    if (isSuitabilityAnalysisLoading) {
      return <LoadingSpinner className="content-spinner" />;
    }

    return this.renderResults();
  };

  renderResults = () => {
    const { portfolios, suitabilityAnalysis } = this.props;
    const { detailedPortfolio } = this.state;
    const mergedPorfolioAnalysisResults = this.mergePorfolioAnalysisResults(suitabilityAnalysis, portfolios);

    return (
      <>
        <Breadcrumb items={this.generateBreadcrumbs()} />
        {!detailedPortfolio ? (
          <PortfoliosTable
            portfolios={mergedPorfolioAnalysisResults}
            onViewDetails={selectedPortfolio => this.setState({ detailedPortfolio: selectedPortfolio })}
          />
        ) : (
          <DetailedPortfolio portfolio={detailedPortfolio} />
        )}
      </>
    );
  };

  runSuitabilityAnalysis = () => {
    const { contactId, portfolios, suitabilityAnalysis } = this.props;
    if (!suitabilityAnalysis || suitabilityAnalysis.length === 0) {
      this.props.getSuitabilityAnalysis(portfolios, contactId).catch(() => this.closeModal());
    }
  };

  closeModal = () => {
    const { onCancel } = this.props;

    this.setState({ detailedPortfolio: null });

    if (onCancel) {
      onCancel();
    }
  };

  render() {
    const { className, show, submitButton } = this.props;

    return (
      <Modal
        show={show}
        onHide={this.closeModal}
        className={classNames('product-suitability-modal', className)}
        dialogClassName="modal-dialog-centered"
        size="lg"
        onEntering={this.runSuitabilityAnalysis}
      >
        <Modal.Header className="d-flex">
          <h1 className="flex-grow-1">
            <FormattedMessage id="ProductSuitability.Title" />
          </h1>
          <CancelButton onClick={this.closeModal}>
            <FormattedMessage id="Common.Cancel" />
          </CancelButton>
          {submitButton}
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">{this.renderBody()}</Modal.Body>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  isSuitabilityAnalysisLoading: selectIsLoadingEvery(state, [GET_SUITABILITY_ANALYSIS.type]),
  suitabilityAnalysis: state.suitabilityAnalysis,
});

export default connect(mapStateToProps, {
  getSuitabilityAnalysis,
})(ProductSuitabilityModal);
